/** Renders server-supplied choices/page links; contains no selection or pagination policy. */
import type { ShopifyResultQuestionNavigation, ShopifyResultPhrases } from '@testfeed/shopify-contracts'
import { ShopifySegmentedFilterLinks } from '../ShopifySegmentedFilterNav'
import { withShopifyAppContext } from '../../resultsNavigation'
import { ResultNavigationButton, useResultNavigation } from './ResultNavigation'

type Choice = ShopifyResultQuestionNavigation['versions'][number]
/** Optional analysis paging uses ordinary native read-only links, not the authored-answer selection action. */
export function ServerResultPaging({ data }: { data: ShopifyResultPhrases['navigation'] }) {
  if (!data) return null
  return (
    <s-stack direction="inline" gap="base" alignItems="center">
      {data.previous && <s-button href={withShopifyAppContext(data.previous.href)}>{data.previous.label}</s-button>}
      <s-text color="subdued">{data.pageLabel}</s-text>
      {data.next && <s-button href={withShopifyAppContext(data.next.href)}>{data.next.label}</s-button>}
    </s-stack>
  )
}
function Choices({ label, choices }: { label: string; choices: Choice[] }) {
  const navigation = useResultNavigation()
  return (
    <s-stack direction="block" gap="small-200">
      <s-text type="strong">{label}</s-text>
      <ShopifySegmentedFilterLinks
        choices={choices.map((choice) => ({ ...choice, href: withShopifyAppContext(choice.href) }))}
        pending={navigation?.answers.status !== 'ready' && navigation !== null}
        onNavigate={(event, href) => navigation?.navigate(event, href, 'answers')}
      />
    </s-stack>
  )
}
export function ServerQuestionNavigation({
  data,
  pagingOnly = false,
}: {
  data: ShopifyResultQuestionNavigation
  pagingOnly?: boolean
}) {
  let choices = null
  if (!pagingOnly)
    choices = (
      <>
        {data.questions.length > 1 && <Choices label={data.questionLabel} choices={data.questions} />}
        <Choices label={data.versionLabel} choices={data.versions} />
      </>
    )
  return (
    <s-stack direction="block" gap="base">
      {choices}
      <s-stack direction="inline" gap="base" alignItems="center">
        {data.previous && (
          <ResultNavigationButton position="answers" href={withShopifyAppContext(data.previous.href)}>
            {data.previous.label}
          </ResultNavigationButton>
        )}
        <s-text color="subdued">{data.pageLabel}</s-text>
        {data.next && (
          <ResultNavigationButton position="answers" href={withShopifyAppContext(data.next.href)}>
            {data.next.label}
          </ResultNavigationButton>
        )}
      </s-stack>
    </s-stack>
  )
}
